<script setup lang="ts">
/**
 * Every dropdown, searchable: type to narrow the choices, arrows and Enter to
 * pick, Escape to leave. A drop-in for `<select>`: the same `<option>` and
 * `<optgroup>` children (v-for and v-if included), `v-model` with the value
 * as a string, and `id`, `aria-label`, `aria-describedby`, `disabled` and
 * `class` passed through.
 *
 * Matching ignores case and accents, and every word typed must appear
 * (« cas ma » finds « Casablanca Maarif », lib/search.ts). The list opens in a
 * popover layered above drawers and dialogs, never clipped by a scrolling panel.
 */
import { Comment, Fragment, Text, computed, nextTick, ref, useAttrs, useId, useSlots, watch, type FunctionalComponent, type VNode } from 'vue'
import { PopoverAnchor, PopoverContent, PopoverPortal, PopoverRoot } from 'reka-ui'
import { useI18n } from 'vue-i18n'
import { cn } from '@/lib/cn'
import { filterOptions } from '@/lib/search'
import { FIELD_CLASSES } from './variants'

defineOptions({ inheritAttrs: false })
const p = defineProps<{ modelValue?: string | number | null; disabled?: boolean; placeholder?: string }>()
const emit = defineEmits<{ 'update:modelValue': [v: string]; change: [v: string] }>()
const attrs = useAttrs()
const slots = useSlots()
const { t } = useI18n()

interface Opt { value: string; label: string; disabled: boolean; group?: string }

function textOf(children: unknown): string {
  if (children == null || typeof children === 'boolean') return ''
  if (typeof children === 'string' || typeof children === 'number') return String(children)
  if (Array.isArray(children)) return children.map((c) => textOf(c)).join('')
  const n = children as VNode
  if (n.type === Comment) return ''
  if (n.type === Text) return String(n.children ?? '')
  return textOf(n.children)
}

const isOn = (v: unknown): boolean => v !== undefined && v !== null && v !== false

function read(nodes: VNode[] | undefined, out: Opt[], group?: string): Opt[] {
  for (const n of nodes ?? []) {
    if (n.type === Fragment) read(n.children as VNode[], out, group)
    else if (n.type === 'optgroup') read(n.children as VNode[], out, (n.props?.label as string | undefined) ?? group)
    else if (n.type === 'option') {
      const label = textOf(n.children).trim()
      const raw = n.props?.value
      out.push({ value: raw === undefined ? label : String(raw ?? ''), label, disabled: isOn(n.props?.disabled), group })
    }
  }
  return out
}

/*
 * The options are read from the slot at render time only (a slot read in a
 * computed or a hook would not be tracked, and Vue warns): the template calls
 * derive(), which also keeps what the keyboard handlers need.
 */
interface Derived {
  options: Opt[]; chosen: Opt | undefined; visible: Opt[]; choosable: Opt[]
  rows: Array<{ o: Opt; header: string | null; index: number }>
}
let last: Derived = { options: [], chosen: undefined, visible: [], choosable: [], rows: [] }
const current = computed(() => (p.modelValue == null ? '' : String(p.modelValue)))

const open = ref(false)
const query = ref('')
const active = ref(0)
const field = ref<HTMLInputElement | null>(null)
const list = ref<HTMLUListElement | null>(null)
const ident = useId()

function derive(): Derived {
  const options = read(slots.default?.(), [])
  const visible = filterOptions(options, query.value)
  const choosable = visible.filter((o) => !o.disabled)
  let group: string | undefined
  const rows = visible.map((o) => {
    const header = o.group && o.group !== group ? o.group : null
    group = o.group
    return { o, header, index: o.disabled ? -1 : choosable.indexOf(o) }
  })
  last = { options, chosen: options.find((o) => o.value === current.value), visible, choosable, rows }
  return last
}
/** Passes a value computed in the render to the slot below it (a `let` for templates). */
const Scope: FunctionalComponent<{ d: Derived }> = (props, { slots: s }) => s.default?.(props)
const optionId = (o: Opt) => `${ident}-${last.choosable.indexOf(o)}`

function show() {
  if (p.disabled) return
  query.value = ''
  const i = last.options.filter((o) => !o.disabled).findIndex((o) => o.value === current.value)
  active.value = Math.max(0, i)
  open.value = true
}
function hide() { open.value = false; query.value = '' }
function choose(o: Opt | undefined) {
  if (!o || o.disabled) return
  if (o.value !== current.value) { emit('update:modelValue', o.value); emit('change', o.value) }
  hide()
}

// The highlighted option stays in view as the arrows move it.
watch([active, open], async () => {
  if (!open.value) return
  await nextTick()
  list.value?.querySelector<HTMLElement>(`[data-index="${active.value}"]`)?.scrollIntoView({ block: 'nearest' })
})

function onKey(e: KeyboardEvent) {
  if (p.disabled) return
  if (!open.value && ['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); show(); return }
  if (!open.value) return
  const last_ = last.choosable.length - 1
  if (e.key === 'ArrowDown') { e.preventDefault(); active.value = Math.min(last_, active.value + 1) }
  else if (e.key === 'ArrowUp') { e.preventDefault(); active.value = Math.max(0, active.value - 1) }
  else if (e.key === 'Home') { e.preventDefault(); active.value = 0 }
  else if (e.key === 'End') { e.preventDefault(); active.value = last_ }
  else if (e.key === 'Enter') { e.preventDefault(); choose(last.choosable[active.value]) }
  else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); hide() }
  else if (e.key === 'Tab') hide()
}
function onInput(e: Event) {
  query.value = (e.target as HTMLInputElement).value
  active.value = 0
  if (!open.value) open.value = true
}

/* As wide as its longest choice, like a native select, unless the screen sets a width. */
const sizeOf = (d: Derived) => Math.min(40, Math.max(6, ...d.options.map((o) => o.label.length))) + 2
const passed = computed(() => {
  const { class: _c, style: _s, ...rest } = attrs
  return rest
})
defineExpose({ focus: () => field.value?.focus() })
</script>

<template>
  <Scope :d="derive()" v-slot="{ d }">
  <PopoverRoot :open="open" @update:open="(o: boolean) => (o ? show() : hide())">
    <PopoverAnchor as-child>
      <span :class="cn('relative inline-grid min-w-0', attrs.class as string)" :style="attrs.style as string">
        <input ref="field" v-bind="passed" :disabled="disabled"
               role="combobox" :aria-expanded="open" :aria-controls="`${ident}-list`" aria-autocomplete="list"
               :aria-activedescendant="open && d.choosable[active] ? optionId(d.choosable[active]!) : undefined"
               autocomplete="off" spellcheck="false" :size="sizeOf(d)"
               :class="cn(FIELD_CLASSES, 'h-9 w-full cursor-default py-1.5 pe-8 focus:cursor-text')"
               :value="open ? query : (d.chosen?.label ?? '')"
               :placeholder="open ? (d.chosen?.label || t('common.searchPlaceholder')) : placeholder"
               @input="onInput" @mousedown="() => { if (!open) show() }" @blur="hide" @keydown="onKey" />
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"
             aria-hidden="true" class="pointer-events-none absolute end-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </span>
    </PopoverAnchor>
    <PopoverPortal>
      <PopoverContent align="start" :side-offset="4" :collision-padding="8"
                      class="z-dropdown max-h-[min(320px,var(--reka-popover-content-available-height))] min-w-[var(--reka-popover-trigger-width)] max-w-[min(480px,calc(100vw-16px))] overflow-auto rounded-card border border-border bg-popover p-1 text-popover-foreground shadow-pop
                             data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[.97] data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-[.97]"
                      @open-auto-focus.prevent @close-auto-focus.prevent
                      @interact-outside="(e: Event) => { if (field?.contains(e.target as Node)) e.preventDefault() }">
        <ul :id="`${ident}-list`" ref="list" role="listbox" :aria-label="attrs['aria-label'] as string | undefined">
          <li v-if="!d.visible.length" class="px-2 py-1.5 text-[13px] text-muted-foreground">{{ t('common.noResultFor', { query }) }}</li>
          <template v-for="r in d.rows" :key="`${r.o.group ?? ''}|${r.o.value}`">
            <li v-if="r.header" role="presentation"
                class="px-2 pb-0.5 pt-1.5 text-[11px] font-semibold uppercase tracking-[.06em] text-muted-foreground">{{ r.header }}</li>
            <!-- Chosen on mouse down, before the field loses the focus and closes the list. -->
            <li :id="r.o.disabled ? undefined : optionId(r.o)" role="option" :data-index="r.index"
                :aria-selected="r.o.value === current" :aria-disabled="r.o.disabled || undefined"
                :class="['cursor-pointer select-none truncate rounded-[5px] px-2 py-1.5 text-[13px]',
                         r.index === active && 'bg-muted', r.o.value === current && 'font-semibold text-primary',
                         r.o.disabled && 'cursor-not-allowed opacity-50']"
                @mousedown.prevent="choose(r.o)" @mouseenter="() => { if (r.index >= 0) active = r.index }">
              {{ r.o.label || ' ' }}
            </li>
          </template>
        </ul>
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
  </Scope>
</template>
